/**
 * Booking Labels Helper
 * Single source of truth for human-readable Arabic labels
 */

/**
 * Get photography type label in Arabic
 * القيم المخزنة في قاعدة البيانات تبقى كما هي (portrait, wedding, ...)
 * هذه الدالة تُستخدم فقط عند العرض للمستخدم
 * @param {string|null|undefined} type - Photography type value from DB
 * @returns {string} Human-readable Arabic label
 */
export const getPhotographyTypeLabelAr = (type) => {
  if (!type) return 'غير محدد';

  const labels = {
    'wedding': 'تصوير حفلات الزفاف',
    'portrait': 'التصوير الشخصي',
    'event': 'تصوير المناسبات',
    'commercial': 'التصوير التجاري',
    'real-estate': 'تصوير العقارات',
    'real_estate': 'تصوير العقارات',
    'food': 'تصوير الطعام',
    'fashion': 'تصوير الأزياء',
    'sports': 'التصوير الرياضي',
    'product': 'تصوير المنتجات',
  };

  return labels[type] ?? type;
};

/**
 * Get workload label in Arabic based on photography type and workload bucket
 * @param {string} photographyType - Photography type (e.g., 'food', 'product')
 * @param {string|null|undefined} workloadBucket - Workload bucket ('one_item', 'small', 'medium', 'large')
 * @returns {string} Human-readable Arabic label
 */
export const getWorkloadLabelAr = (photographyType, workloadBucket) => {
  if (!workloadBucket) {
    return 'غير محدد';
  }

  // Food photography labels
  if (photographyType === 'food') {
    const labels = {
      one_item: 'عدد قليل — منيو صغير أو منتجات محدودة (حجز قديم)',
      small: 'عدد قليل — 1 إلى 5 عناصر — منيو صغير أو منتجات محدودة',
      medium: 'عدد متوسط — 6 إلى 15 عنصر — منيو متوسط أو مجموعة منتجات',
      large: 'عدد كبير — أكثر من 15 عنصر — منيو كامل أو قائمة كبيرة',
    };
    return labels[workloadBucket] || workloadBucket;
  }

  // حفلات الزفاف (أفراد) — نوع التغطية
  if (photographyType === 'wedding') {
    const labels = {
      wedding_event_only: 'تغطية الحفل — تغطية مجريات الحفل خلال وقت إقامته',
      wedding_prep_and_event: 'تغطية التجهيز والحفل — توثيق مراحل التجهيز بالإضافة إلى الحفل',
      wedding_full_coverage: 'تغطية شاملة — توثيق كامل لمراحل المناسبة من البداية حتى النهاية',
    };
    return labels[workloadBucket] || workloadBucket;
  }

  // التصوير الشخصي (أفراد) — عدد الأشخاص في الجلسة
  if (photographyType === 'portrait') {
    const labels = {
      portrait_one: 'شخص واحد — تصوير شخصي، بورتريه، صور سوشيال، صورة مهنية',
      portrait_two: 'شخصين — أصدقاء، شخصين معًا في نفس الجلسة',
      portrait_group: '3 أشخاص وأكثر — عائلة، مجموعة أصدقاء، عدة أشخاص',
    };
    return labels[workloadBucket] || workloadBucket;
  }

  // Product photography labels (عدد المنتجات — ليس عدد الصور)
  if (photographyType === 'product') {
    const labels = {
      one_item: 'عنصر واحد (حجز قديم)',
      small: 'عدد قليل — من 1 إلى 5 منتجات',
      medium: 'عدد متوسط — من 6 إلى 10 منتجات',
      large: 'عدد كبير — من 11 إلى 20 منتج',
      xlarge: 'عدد كبير جداً — من 21 إلى 30 منتج',
    };
    return labels[workloadBucket] || workloadBucket;
  }

  // Event photography — طبيعة الفعالية (نفس مفاتيح small / medium / large للتسعير)
  if (photographyType === 'event') {
    const labels = {
      small: 'فعالية بسيطة — لقاء، افتتاح بسيط، حفل صغير',
      medium: 'فعالية منظمة — مؤتمر، ورشة، حفل منظم بجدول',
      large: 'فعالية إنتاجية — مؤتمر كبير، مهرجان، فعالية متعددة الأنشطة',
    };
    return labels[workloadBucket] || workloadBucket;
  }

  // Real estate — طبيعة العقار
  if (photographyType === 'real-estate' || photographyType === 'real_estate') {
    const labels = {
      small: 'عقار بسيط — شقة، محل صغير، مكتب صغير…',
      medium: 'عقار متوسط — فيلا، دوبلكس، مكتب كبير…',
      large: 'عقار كبير — قصر، مجمع، مشروع…',
    };
    return labels[workloadBucket] || workloadBucket;
  }

  // Default labels for other photography types
  const defaultLabels = {
    'one_item': 'عنصر واحد',
    'small': 'صغير (2-5 عناصر)',
    'medium': 'متوسط (6-10 عناصر)',
    'large': 'كبير (أكثر من 10)'
  };
  return defaultLabels[workloadBucket] || workloadBucket;
};

/**
 * حجم المنتجات الفعلي (تصوير منتجات) — للعرض في الملخص والحجوزات
 * @param {string|null|undefined} bucket - compact | standard | bulky
 */
export const getProductPhysicalSizeLabelAr = (bucket) => {
  if (!bucket) return 'غير محدد';

  const labels = {
    compact:
      'صغيرة الحجم — سهلة النقل ولا تحتاج مساحة — عطور، إكسسوارات، ساعات، أدوات تجميل، جوالات، منتجات صغيرة',
    standard:
      'متوسطة الحجم — سهلة الحمل وبمساحة متوسطة — ملابس، حقائب، أجهزة إلكترونية، أدوات منزلية',
    bulky:
      'كبيرة الحجم — صعبة الحمل والنقل وبمساحة كبيرة — أثاث، معدات، أجهزة كبيرة، منتجات بحجم كبير',
  };

  return labels[bucket] || bucket;
};

/**
 * Get delivered photos label in Arabic
 * @param {string|null|undefined} deliveredPhotosBucket - Delivered photos bucket
 * @returns {string} Human-readable Arabic label
 */
export const getDeliveredPhotosLabelAr = (deliveredPhotosBucket) => {
  if (!deliveredPhotosBucket) {
    return 'غير محدد';
  }

  const labels = {
    'basic': 'أساسي (10-20 صورة)',
    'medium': 'متوسط (30-50 صورة)',
    'large': 'كبير (60-100 صورة)',
    'photographer_decides': 'المصور يقرر'
  };

  return labels[deliveredPhotosBucket] || deliveredPhotosBucket;
};

/**
 * Get shooting place type label in Arabic
 * @param {string|null|undefined} placeType - Place type key
 * @returns {string} Human-readable Arabic label
 */
export const getShootingPlaceTypeLabelAr = (placeType) => {
  if (!placeType) return 'غير محدد';
  const labels = {
    'indoor':             'داخلي',
    'outdoor':            'خارجي',
    'indoor_outdoor':     'داخلي وخارجي',
    'photographer_studio':'في استديو المصور',
  };
  return labels[placeType] || placeType;
};

/**
 * Get shooting method label in Arabic
 * @param {string|null|undefined} shootingMethod - Shooting method ('professional_camera', 'mobile_camera', 'drone')
 * @returns {string} Human-readable Arabic label
 */
export const getShootingMethodLabelAr = (shootingMethod) => {
  if (!shootingMethod) {
    return 'غير محدد';
  }

  const labels = {
    'professional_camera': 'كاميرا احترافية',
    'mobile_camera': 'كاميرا جوال',
    'drone': 'تصوير جوي (درون)'
  };

  return labels[shootingMethod] || shootingMethod;
};
